/**
 * Data formatting utilities
 */

/**
 * Formats data values for display, handling arrays and objects
 */
export const formatDataValue = (value: any): string => {
    if (Array.isArray(value)) {
        return value.map(item => formatDataValue(item)).join(', ');
    }

    if (typeof value === 'object' && value !== null) {
        const entries = Object.entries(value);
        const isArrayLikeWithNumericKeys = entries.every(([k]) => !isNaN(Number(k)));

        if (isArrayLikeWithNumericKeys && entries.length > 0) {
            return entries.map(([, v]) => formatDataValue(v)).join(', ');
        } else {
            return entries.map(([k, v]) => `${k}: ${formatDataValue(v)}`).join('; ');
        }
    }

    return String(value);
};

/**
 * Checks if text contains special characters
 */
export const hasSpecialCharacters = (searchText: string): boolean => {
    return /[^a-zA-Z0-9\s]/.test(searchText);
};

/**
 * Check if any string in the array includes the specified key (case-insensitive)
 * @param array - Array of strings to search through
 * @param key - Key to search for
 * @returns True if any string in the array includes the key
 */
export const checkIfArrayStringsIncludesKey = (array: string[], key: string): boolean => {
    if (!array || !key) return false;
    return array.some(item => item && typeof item === 'string' && item.toLowerCase().includes(key.toLowerCase()));
};

/**
 * Formats bytes to human-readable format (B, KB, MB, GB, TB)
 * @param bytes - Number of bytes to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with appropriate unit
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
    if (bytes === 0 || !bytes) return '0 B';
    if (bytes < 0) return '-' + formatBytes(-bytes, decimals);

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const index = Math.min(i, sizes.length - 1);

    const value = bytes / Math.pow(k, index);
    const formatted = parseFloat(value.toFixed(dm));

    return formatted + ' ' + sizes[index];
};

/**
 * Checks if a metric type is related to byte measurements
 * @param metricType - The metric type to check
 * @returns True if the metric is byte-related
 */
export const isByteMetric = (metricType: string): boolean => {
    return (
        metricType.includes('bytes') ||
        metricType.includes('size') ||
        metricType.includes('deleted-data') ||
        metricType.includes('uploaded-data') ||
        metricType.includes('downloaded-data') ||
        metricType.includes('upload') ||
        metricType.includes('download') ||
        metricType.includes('delete')
    );
};

/**
 * Sanitizes a path string by removing spaces and leading/trailing slashes
 * @param path - The path to sanitize
 * @returns sanitized path string
 */
export const sanitizePath = (path: string | undefined): string => {
    if (!path) return '';
    return path.replace(/\s+/g, '').replace(/\/+/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
};

/**
 * Sanitizes a workflow name/ID by replacing spaces and non-alphanumeric characters with underscores
 * @param wfId - The workflow ID to sanitize
 * @returns sanitized workflow ID string
 */
export const sanitizeWorkflowId = (wfId: string | undefined): string => {
    if (!wfId) return '';
    return wfId.replace(/[^a-zA-Z0-9]+| {2,}/g, '_');
};

